import {useState, useEffect} from 'react';
import postAddField from '../functions/API/postAddField.js'
import postUpdateField from '../functions/API/postUpdateField.js'
import { toast } from '../lib/toast.js';
import { Switch } from '../components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Button } from '../components/ui/button'
import { FIELD_TYPES, FIELD_TYPE_OPTIONS, DEFAULT_VALIDATION_RULES } from '../constants/fieldTypes.js'
import { X, Plus } from 'lucide-react'

// Helper function to parse validation rules from existing data
function parseExistingValidationRules(existingData) {
    const fieldTypeValue = existingData?.field_type || 'TEXT';

    // Parse validation_rules if it's a string
    let validationRules = existingData?.validation_rules;
    if (typeof validationRules === 'string') {
        try {
            validationRules = JSON.parse(validationRules);
        } catch (e) {
            validationRules = {...DEFAULT_VALIDATION_RULES[fieldTypeValue]};
        }
    } else if (!validationRules) {
        validationRules = {...DEFAULT_VALIDATION_RULES[fieldTypeValue]};
    }

    // Parse options if they're a string (for DROPDOWN/MULTI_SELECT)
    if ((fieldTypeValue === 'DROPDOWN' || fieldTypeValue === 'MULTI_SELECT') && validationRules.options) {
        if (typeof validationRules.options === 'string') {
            try {
                validationRules.options = JSON.parse(validationRules.options);
            } catch (e) {
                validationRules.options = [];
            }
        }
    }

    // Ensure validation rules has the correct structure and types
    validationRules = {...DEFAULT_VALIDATION_RULES[fieldTypeValue], ...validationRules};

    // Convert boolean values properly (handle strings, numbers, etc.)
    if (fieldTypeValue === 'DATE') {
        validationRules.allow_past = validationRules.allow_past === true || validationRules.allow_past === 'true' || validationRules.allow_past === 1;
        validationRules.allow_future = validationRules.allow_future === true || validationRules.allow_future === 'true' || validationRules.allow_future === 1;
    } else if (fieldTypeValue === 'NUMBER' || fieldTypeValue === 'CURRENCY') {
        validationRules.allow_decimals = validationRules.allow_decimals === true || validationRules.allow_decimals === 'true' || validationRules.allow_decimals === 1;
    } else if (fieldTypeValue === 'BOOLEAN') {
        validationRules.default_value = validationRules.default_value === true || validationRules.default_value === 'true' || validationRules.default_value === 1;
    }

    return validationRules;
}

// Helper function to get initial select options from existing data
function getInitialSelectOptions(existingData) {
    if (!existingData) return [{value: '', label: ''}];

    const fieldTypeValue = existingData.field_type || 'TEXT';
    if (fieldTypeValue !== 'DROPDOWN' && fieldTypeValue !== 'MULTI_SELECT') {
        return [{value: '', label: ''}];
    }

    const validationRules = parseExistingValidationRules(existingData);
    if (validationRules?.options && Array.isArray(validationRules.options) && validationRules.options.length > 0) {
        return JSON.parse(JSON.stringify(validationRules.options));
    }
    return [{value: '', label: ''}];
}

export default function NewFieldForm({setVisible, refresh, refreshValue, stepId, editMode = false, existingData = null}) {
    // Initialize state directly from props for edit mode
    const [fieldType, setFieldType] = useState(() => {
        if (editMode && existingData) {
            return existingData.field_type || 'TEXT';
        }
        return 'TEXT';
    });

    const [newField, setNewField] = useState(() => {
        if (editMode && existingData) {
            const fieldTypeValue = existingData.field_type || 'TEXT';
            const fieldLabel = existingData.name || existingData.field_label || "";
            const validationRules = parseExistingValidationRules(existingData);

            return {
                id: existingData.id || "",
                step_id: existingData.task_flow_step_id || existingData.step_id || stepId,
                field_label: fieldLabel,
                field_type: fieldTypeValue,
                is_required: existingData.is_required !== undefined ? existingData.is_required : true,
                placeholder: existingData.placeholder || "",
                help_text: existingData.help_text || "",
                help_url: existingData.help_url || "",
                validation_rules: validationRules
            };
        }
        return {
            id: "",
            step_id: stepId,
            field_label: "",
            field_type: "TEXT",
            is_required: true,
            placeholder: "",
            help_text: "",
            help_url: "",
            validation_rules: {...DEFAULT_VALIDATION_RULES.TEXT}
        };
    });

    // Options for DROPDOWN and MULTI_SELECT field types
    const [selectOptions, setSelectOptions] = useState(() => {
        if (editMode && existingData) {
            return getInitialSelectOptions(existingData);
        }
        return [{value: '', label: ''}];
    });

    // Update validation rules when field type changes (only in add mode)
    useEffect(() => {
        if (!editMode) {
            const fieldTypeConfig = FIELD_TYPES[fieldType] || FIELD_TYPES.TEXT;
            setNewField(prev => ({
                ...prev,
                field_type: fieldType,
                validation_rules: {...DEFAULT_VALIDATION_RULES[fieldType]},
                placeholder: fieldTypeConfig.hasPlaceholder ? prev.placeholder : ""
            }));

            // Reset select options when changing to DROPDOWN or MULTI_SELECT (only in add mode)
            if (fieldType === 'DROPDOWN' || fieldType === 'MULTI_SELECT') {
                setSelectOptions([{value: '', label: ''}]);
            }
        }
    }, [fieldType, editMode]);

    async function createNewField(){
        // Validate required fields
        if (!newField.field_label) {
            toast.error('Field label is required.');
            return;
        }

        // Validate DROPDOWN and MULTI_SELECT options
        if (newField.field_type === 'DROPDOWN' || newField.field_type === 'MULTI_SELECT') {
            const validOptions = selectOptions.filter(opt => opt.value && opt.label);
            if (validOptions.length === 0) {
                toast.error(`At least one option is required for ${newField.field_type === 'DROPDOWN' ? 'DROPDOWN' : 'MULTI_SELECT'} fields.`);
                return;
            }
            newField.validation_rules.options = validOptions;
        }

        const body = {
            task_flow_step_id: newField.step_id || stepId,
            name: newField.field_label,
            field_type: newField.field_type,
            is_required: newField.is_required,
            placeholder: newField.placeholder || null,
            help_text: newField.help_text || null,
            help_url: newField.help_url || null,
            validation_rules: newField.validation_rules
        };

        // Add ID for update operations
        if (editMode) {
            body.id = newField.id;
        }

        const apiFunction = editMode ? postUpdateField : postAddField;
        const successMessage = editMode ? 'Field updated successfully.' : 'Field added successfully.';

        const FieldResponse = await apiFunction(body);
        if(FieldResponse.response.ok){
            refresh(refreshValue+1);
            toast.success(successMessage);
            setVisible(false);
        }else{
            toast.error('Sorry an error occurred. Try again later.');
        }
    }

    function handleChange (event) {
        let statechange = event.target.name;
        let value = event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value;
        setNewField({...newField, [statechange]: value});
    }

    function handleSwitchChange(name, checked) {
        setNewField({...newField, [name]: checked});
    }

    function handleValidationChange(name, value) {
        setNewField({
            ...newField,
            validation_rules: {
                ...newField.validation_rules,
                [name]: value
            }
        });
    }

    function handleValidationSwitch(name, checked) {
        setNewField({
            ...newField,
            validation_rules: {
                ...newField.validation_rules,
                [name]: checked
            }
        });
    }

    // SELECT options handlers
    function addSelectOption() {
        setSelectOptions([...selectOptions, {value: '', label: ''}]);
    }

    function removeSelectOption(index) {
        const newOptions = selectOptions.filter((_, i) => i !== index);
        setSelectOptions(newOptions.length > 0 ? newOptions : [{value: '', label: ''}]);
    }

    function updateSelectOption(index, field, value) {
        const newOptions = [...selectOptions];
        newOptions[index][field] = value;
        setSelectOptions(newOptions);
    }

    const selectedFieldType = FIELD_TYPES[fieldType] || FIELD_TYPES.TEXT;

    return (
        <form>
            <div className="space-y-4">
                {/* Field Label & Type side by side */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="field_label" className="block text-sm font-medium text-gray-900 mb-1">
                            Field Label *
                        </label>
                        <input
                            required
                            type="text"
                            onChange={handleChange}
                            value={newField.field_label}
                            name="field_label"
                            id="field_label"
                            placeholder="e.g., Full Name"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="field_type" className="block text-sm font-medium text-gray-900 mb-1">
                            Field Type *
                        </label>
                        <Select value={fieldType} onValueChange={setFieldType} disabled={editMode}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select field type" />
                            </SelectTrigger>
                            <SelectContent>
                                {FIELD_TYPE_OPTIONS.map(type => {
                                    const Icon = type.icon;
                                    return (
                                        <SelectItem key={type.value} value={type.value}>
                                            <div className="flex items-center gap-2">
                                                <Icon className="h-4 w-4" />
                                                <span>{type.label}</span>
                                            </div>
                                        </SelectItem>
                                    );
                                })}
                            </SelectContent>
                        </Select>
                        {selectedFieldType && (
                            <p className="mt-0.5 text-xs text-gray-500">{selectedFieldType.description}</p>
                        )}
                    </div>
                </div>

                {/* Required Toggle */}
                <div className="flex items-center justify-between">
                    <label htmlFor="is_required" className="text-sm font-medium text-gray-900">
                        Required Field
                    </label>
                    <Switch
                        id="is_required"
                        checked={newField.is_required}
                        onCheckedChange={(checked) => handleSwitchChange('is_required', checked)}
                    />
                </div>

                {/* Placeholder (conditional) */}
                {selectedFieldType?.hasPlaceholder && (
                    <div>
                        <label htmlFor="placeholder" className="block text-sm font-medium text-gray-900 mb-1">
                            Placeholder
                        </label>
                        <input
                            type="text"
                            onChange={handleChange}
                            value={newField.placeholder}
                            name="placeholder"
                            id="placeholder"
                            placeholder="Enter placeholder text"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                        />
                    </div>
                )}

                {/* Help Text & URL side by side */}
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="help_text" className="block text-sm font-medium text-gray-900 mb-1">
                            Help Text
                        </label>
                        <input
                            type="text"
                            onChange={handleChange}
                            value={newField.help_text}
                            name="help_text"
                            id="help_text"
                            placeholder="Guidance for users"
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label htmlFor="help_url" className="block text-sm font-medium text-gray-900 mb-1">
                            Help URL
                        </label>
                        <input
                            type="url"
                            onChange={handleChange}
                            value={newField.help_url}
                            name="help_url"
                            id="help_url"
                            placeholder="https://..."
                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                        />
                    </div>
                </div>

                {/* Validation Rules Section */}
                <div className="border-t pt-3">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Validation Rules</h3>

                    {/* TEXT validation */}
                    {fieldType === 'TEXT' && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Min Length</label>
                                    <input
                                        type="number"
                                        value={newField.validation_rules.min_length || ''}
                                        onChange={(e) => handleValidationChange('min_length', e.target.value ? parseInt(e.target.value) : null)}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Max Length</label>
                                    <input
                                        type="number"
                                        value={newField.validation_rules.max_length || ''}
                                        onChange={(e) => handleValidationChange('max_length', e.target.value ? parseInt(e.target.value) : null)}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                        min="1"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-1">Pattern (Regex)</label>
                                <input
                                    type="text"
                                    value={newField.validation_rules.pattern || ''}
                                    onChange={(e) => handleValidationChange('pattern', e.target.value || null)}
                                    placeholder="e.g., ^[a-zA-Z\s]+$"
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                />
                            </div>
                        </div>
                    )}

                    {/* TEXTAREA validation */}
                    {fieldType === 'TEXTAREA' && (
                        <div className="grid grid-cols-3 gap-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-1">Min Length</label>
                                <input
                                    type="number"
                                    value={newField.validation_rules.min_length || ''}
                                    onChange={(e) => handleValidationChange('min_length', e.target.value ? parseInt(e.target.value) : null)}
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                    min="0"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-1">Max Length</label>
                                <input
                                    type="number"
                                    value={newField.validation_rules.max_length || ''}
                                    onChange={(e) => handleValidationChange('max_length', e.target.value ? parseInt(e.target.value) : null)}
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                    min="1"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-900 mb-1">Max Rows</label>
                                <input
                                    type="number"
                                    value={newField.validation_rules.max_rows || ''}
                                    onChange={(e) => handleValidationChange('max_rows', e.target.value ? parseInt(e.target.value) : null)}
                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                    min="1"
                                />
                            </div>
                        </div>
                    )}

                    {/* DATE validation */}
                    {fieldType === 'DATE' && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Min Date</label>
                                    <input
                                        type="date"
                                        value={newField.validation_rules.min_date || ''}
                                        onChange={(e) => handleValidationChange('min_date', e.target.value || null)}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Max Date</label>
                                    <input
                                        type="date"
                                        value={newField.validation_rules.max_date || ''}
                                        onChange={(e) => handleValidationChange('max_date', e.target.value || null)}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-900">Allow Past Dates</label>
                                <Switch
                                    checked={!!newField.validation_rules.allow_past}
                                    onCheckedChange={(checked) => handleValidationSwitch('allow_past', checked)}
                                />
                            </div>
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-900">Allow Future Dates</label>
                                <Switch
                                    checked={!!newField.validation_rules.allow_future}
                                    onCheckedChange={(checked) => handleValidationSwitch('allow_future', checked)}
                                />
                            </div>
                        </div>
                    )}

                    {/* NUMBER validation */}
                    {fieldType === 'NUMBER' && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Min Value</label>
                                    <input
                                        type="number"
                                        value={newField.validation_rules.min_value || ''}
                                        onChange={(e) => handleValidationChange('min_value', e.target.value ? parseFloat(e.target.value) : null)}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                        step="any"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Max Value</label>
                                    <input
                                        type="number"
                                        value={newField.validation_rules.max_value || ''}
                                        onChange={(e) => handleValidationChange('max_value', e.target.value ? parseFloat(e.target.value) : null)}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                        step="any"
                                    />
                                </div>
                            </div>
                            <div className="flex items-center justify-between">
                                <label className="text-sm font-medium text-gray-900">Allow Decimals</label>
                                <Switch
                                    checked={!!newField.validation_rules.allow_decimals}
                                    onCheckedChange={(checked) => handleValidationSwitch('allow_decimals', checked)}
                                />
                            </div>
                        </div>
                    )}

                    {/* CURRENCY validation */}
                    {fieldType === 'CURRENCY' && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Min Value (£)</label>
                                    <input
                                        type="number"
                                        value={newField.validation_rules.min_value || ''}
                                        onChange={(e) => handleValidationChange('min_value', e.target.value ? parseFloat(e.target.value) : null)}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                        step="0.01"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Max Value (£)</label>
                                    <input
                                        type="number"
                                        value={newField.validation_rules.max_value || ''}
                                        onChange={(e) => handleValidationChange('max_value', e.target.value ? parseFloat(e.target.value) : null)}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                        step="0.01"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* BOOLEAN validation */}
                    {fieldType === 'BOOLEAN' && (
                        <div className="flex items-center justify-between">
                            <label className="text-sm font-medium text-gray-900">Default Value</label>
                            <Switch
                                checked={!!newField.validation_rules.default_value}
                                onCheckedChange={(checked) => handleValidationSwitch('default_value', checked)}
                            />
                        </div>
                    )}

                    {/* DROPDOWN validation */}
                    {fieldType === 'DROPDOWN' && (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-medium text-gray-900">Options *</label>
                                <Button type="button" size="sm" variant="outline" onClick={addSelectOption}>
                                    <Plus className="h-3.5 w-3.5 mr-1" />
                                    Add
                                </Button>
                            </div>
                            {selectOptions.map((option, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={option.value}
                                        onChange={(e) => updateSelectOption(index, 'value', e.target.value)}
                                        placeholder="Value"
                                        className="block w-1/2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                    />
                                    <input
                                        type="text"
                                        value={option.label}
                                        onChange={(e) => updateSelectOption(index, 'label', e.target.value)}
                                        placeholder="Label"
                                        className="block w-1/2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                    />
                                    <Button type="button" variant="ghost" size="icon" onClick={() => removeSelectOption(index)} disabled={selectOptions.length === 1}>
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* MULTI_SELECT validation */}
                    {fieldType === 'MULTI_SELECT' && (
                        <div className="space-y-2">
                            <div className="flex items-center justify-between">
                                <label className="block text-sm font-medium text-gray-900">Options *</label>
                                <Button type="button" size="sm" variant="outline" onClick={addSelectOption}>
                                    <Plus className="h-3.5 w-3.5 mr-1" />
                                    Add
                                </Button>
                            </div>
                            {selectOptions.map((option, index) => (
                                <div key={index} className="flex gap-2">
                                    <input
                                        type="text"
                                        value={option.value}
                                        onChange={(e) => updateSelectOption(index, 'value', e.target.value)}
                                        placeholder="Value"
                                        className="block w-1/2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                    />
                                    <input
                                        type="text"
                                        value={option.label}
                                        onChange={(e) => updateSelectOption(index, 'label', e.target.value)}
                                        placeholder="Label"
                                        className="block w-1/2 rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                    />
                                    <Button type="button" variant="ghost" size="icon" onClick={() => removeSelectOption(index)} disabled={selectOptions.length === 1}>
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            ))}
                            <div className="grid grid-cols-2 gap-3 pt-2">
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Min Selections</label>
                                    <input
                                        type="number"
                                        value={newField.validation_rules.min_selections || ''}
                                        onChange={(e) => handleValidationChange('min_selections', e.target.value ? parseInt(e.target.value) : null)}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-900 mb-1">Max Selections</label>
                                    <input
                                        type="number"
                                        value={newField.validation_rules.max_selections || ''}
                                        onChange={(e) => handleValidationChange('max_selections', e.target.value ? parseInt(e.target.value) : null)}
                                        className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm"
                                        min="1"
                                    />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
                <button
                    type="button"
                    onClick={()=>{createNewField()}}
                    className="rounded-md bg-blue-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700"
                >
                    {editMode ? 'Update Field' : 'Add Field'}
                </button>
                <button type="button" onClick={()=>{setVisible(false)}} className="text-sm font-semibold leading-6 text-gray-900">
                    Cancel
                </button>
            </div>
        </form>
    );
}
